import RefreshIcon from '@mui/icons-material/Refresh';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import MasterFilter, {
  type DateRangeValue,
  type FilterFieldConfig,
  useMasterFilter,
} from 'components/common/MasterFilter';
import PaginationFooter from 'components/common/PaginationFooter';
import PillTabs, { type PillTab } from 'components/common/PillTabs';
import { useSelectedBusinessUnit } from 'contexts/SidebarContext';
import moment from 'moment';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useGetWhatsAppAlertsQuery,
  useGetWhatsAppHealthQuery,
  useRefreshWhatsAppAlertsMutation,
} from 'services/api/whatsAppHealth.api';
import type { QualityRating, WhatsAppAlertSeverity, WhatsAppHealthAlert } from 'services/types/metrics';
import {
  MOBILE_PAGE_PT,
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  RADIUS_ITEM,
  SHADOW,
  STATUS_ERROR,
  STATUS_GOOD,
  STATUS_GOOD_TEXT,
  STATUS_WARN,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
} from 'styles/tokens';

import ChartCard from '../Dashboard/components/ChartCard';
import DashboardHero from '../Dashboard/components/DashboardHero';

/**
 * Spanish relative time, written out rather than delegated to `moment().fromNow()`.
 *
 * Vite pre-bundles `moment` and `moment/locale/es` as separate dependencies, each carrying its
 * own moment instance, so the locale registers on a copy this module never uses — `fromNow()`
 * silently falls back to English, and `moment.locale('es')` cannot fix it either.
 */
const relativeTimeEs = (isoDate: string): string => {
  const minutes = Math.round((Date.now() - new Date(isoDate).getTime()) / 60_000);
  if (minutes < 1) return 'recién';
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.round(hours / 24);
  if (days < 31) return days === 1 ? 'hace 1 día' : `hace ${days} días`;
  const months = Math.round(days / 30);
  return months === 1 ? 'hace 1 mes' : `hace ${months} meses`;
};

const QUALITY_COLORS: Record<QualityRating, string> = {
  GREEN: STATUS_GOOD,
  YELLOW: STATUS_WARN,
  RED: STATUS_ERROR,
  UNKNOWN: alpha(NAVY, 0.25),
};

/**
 * The hero states the rating in 4rem type, so it takes the ramp's legible variants: the fill green
 * is under the 3:1 large text still has to clear, and a muted grey would make "sin datos" read as
 * a disabled control rather than as the headline of the page.
 */
const QUALITY_HERO_COLORS: Record<QualityRating, string> = {
  GREEN: STATUS_GOOD_TEXT,
  YELLOW: STATUS_WARN,
  RED: STATUS_ERROR,
  UNKNOWN: NAVY,
};

const QUALITY_LABELS: Record<QualityRating, string> = {
  GREEN: 'Alta',
  YELLOW: 'Media',
  RED: 'Baja',
  UNKNOWN: '—',
};

/**
 * The redundant encoding that makes the trend readable without colour vision: a worse rating is
 * a shorter bar. The design system forbids carrying meaning on hue alone, and a red/green ramp
 * is the exact case it calls out.
 */
const QUALITY_HEIGHTS: Record<QualityRating, string> = {
  GREEN: '100%',
  YELLOW: '62%',
  RED: '32%',
  UNKNOWN: '18%',
};

/** A fixed gap eats the whole width once the window is wide and the viewport is narrow. */
const STRIP_GAP = (days: number): string => (days > 60 ? '1px' : days > 40 ? '2px' : '3px');

/** Matches the uppercase label used by HardMetricCard so both read as one family. */
const CARD_LABEL_SX = {
  fontFamily: 'Jost, sans-serif',
  fontWeight: 600,
  fontSize: '0.7rem',
  letterSpacing: '0.06em',
  textTransform: 'uppercase' as const,
  color: `${NAVY}99`,
  lineHeight: 1.3,
};

/** Meta reports tiers as TIER_10K; the dashboard shows the human number. */
const formatLimit = (limit: string | null): { value: string; caption: string } => {
  if (!limit) return { value: '—', caption: 'sin reportar' };
  if (limit === 'TIER_UNLIMITED') return { value: 'Sin límite', caption: 'conversaciones al día' };
  if (limit === 'TIER_NOT_SET') return { value: '—', caption: 'sin definir' };
  // The K is a multiplier: Meta reports the 250 tier as TIER_0.25K, which substituting zeros
  // would turn into 0,25.
  const raw = limit.replace('TIER_', '');
  const parsed = raw.endsWith('K') ? Number(raw.slice(0, -1)) * 1000 : Number(raw);
  return Number.isFinite(parsed)
    ? { value: parsed.toLocaleString('es-CL'), caption: 'conversaciones al día' }
    : { value: limit, caption: 'conversaciones al día' };
};

/**
 * Text-valued sibling of HardMetricCard — same surface, radius, shadow and label treatment,
 * for the states that read as a word rather than a number.
 */
const StatusCard: React.FC<{ label: string; value: string; caption?: string; accent?: string }> = ({
  label,
  value,
  caption,
  accent,
}) => (
  <Box
    sx={{
      minHeight: 124,
      height: '100%',
      p: 2.25,
      borderRadius: RADIUS_COIN,
      boxShadow: SHADOW,
      backgroundColor: SURFACE_CONTAINER_LOWEST,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    }}
  >
    <Typography component="h3" sx={CARD_LABEL_SX}>
      {label}
    </Typography>
    <Box sx={{ mt: 1 }}>
      <Typography
        sx={{ fontFamily: 'Jost, sans-serif', fontSize: '2rem', fontWeight: 700, lineHeight: 1, color: accent ?? NAVY }}
      >
        {value}
      </Typography>
      {caption && (
        <Typography sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.8rem', color: alpha(NAVY, 0.55), mt: 0.5 }}>
          {caption}
        </Typography>
      )}
    </Box>
  </Box>
);

const QualityLegend: React.FC = () => (
  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
    {(['GREEN', 'YELLOW', 'RED', 'UNKNOWN'] as const).map((rating) => (
      <Box key={rating} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {/* Swatches mirror both channels of the strip — the same height ramp and the same 0.8
            alpha — so the legend can actually disambiguate what the chart is showing. */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', height: 12 }}>
          <Box
            sx={{
              width: 10,
              height: QUALITY_HEIGHTS[rating],
              borderRadius: '3px',
              backgroundColor: rating === 'UNKNOWN' ? QUALITY_COLORS.UNKNOWN : alpha(QUALITY_COLORS[rating], 0.8),
            }}
          />
        </Box>
        <Typography sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.78rem', color: alpha(NAVY, 0.6) }}>
          {rating === 'UNKNOWN' ? 'Sin datos' : QUALITY_LABELS[rating]}
        </Typography>
      </Box>
    ))}
  </Box>
);

const SEVERITY_COLORS: Record<WhatsAppAlertSeverity, string> = {
  critical: STATUS_ERROR,
  warning: STATUS_WARN,
  info: alpha(NAVY, 0.3),
};

const SEVERITY_LABELS: Record<WhatsAppAlertSeverity, string> = {
  critical: 'Crítica',
  warning: 'Advertencia',
  info: 'Informativa',
};

export const CATEGORY_LABELS: Record<string, string> = {
  quality: 'Calidad',
  account: 'Cuenta',
  template: 'Plantillas',
  capability: 'Límites',
  channel: 'Canal',
  other: 'Otros',
};

type HealthAlert = WhatsAppHealthAlert;

/**
 * The window is a pair of calendar days, so it travels as days. Sending an instant made the
 * browser's timezone part of the request: a Chile-local end-of-day lands on the next UTC day, and
 * the series then ran one cell past the period its own caption described.
 */
const API_DATE_FORMAT = 'YYYY-MM-DD';

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50];

const ALL_TYPES = 'all';

/**
 * Even by design: an odd size leaves the circle straddling half a device pixel once the browser
 * zoom is not 100%, and it then rasterises a pixel wider than it is tall — a visibly oval dot.
 */
const DOT_SIZE = 10;
/** Width of the card-coloured ring that masks the rail where it passes behind a dot. */
const DOT_RING = 3;
const RAIL_WIDTH = 2;
/** Padding of a timeline row, in px. The dot lives inside it, ring included. */
const ROW_PAD_X = 12;
const ROW_PAD_Y = 10;
/** Distance from the top of a row to the dot, chosen to centre it on the timestamp line. */
const DOT_OFFSET_TOP = 4;
const RAIL_LEFT = ROW_PAD_X + (DOT_SIZE - RAIL_WIDTH) / 2;
/** The rail spans dot centre to dot centre, so it starts and ends inside the first and last rows. */
const RAIL_INSET = ROW_PAD_Y + DOT_OFFSET_TOP + DOT_SIZE / 2;

/**
 * Vertical timeline: a soft rail with one dot per event, newest first. The rail is the only
 * stroke on the page — it expresses the sequence, so it is data rather than a section divider.
 */
const AlertTimeline: React.FC<{ alerts: HealthAlert[] }> = ({ alerts }) => (
  <Box sx={{ position: 'relative' }}>
    {alerts.length > 1 && (
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          left: RAIL_LEFT,
          top: RAIL_INSET,
          bottom: RAIL_INSET,
          width: RAIL_WIDTH,
          borderRadius: 1,
          backgroundColor: alpha(NAVY, 0.08),
        }}
      />
    )}
    {alerts.map((alert) => (
      <Box
        key={alert.id}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          py: `${ROW_PAD_Y}px`,
          px: `${ROW_PAD_X}px`,
          borderRadius: RADIUS_ITEM,
          '&:hover': { backgroundColor: alpha(NAVY, 0.03) },
        }}
      >
        <Box
          sx={{
            width: DOT_SIZE,
            height: DOT_SIZE,
            mt: `${DOT_OFFSET_TOP}px`,
            flexShrink: 0,
            borderRadius: '50%',
            backgroundColor: SEVERITY_COLORS[alert.severity],
            // Ring in the card colour so the dot reads as sitting on top of the rail. With a single
            // event there is no rail to mask and the ring is just a halo, so it only renders when
            // there is something behind the dot.
            boxShadow: alerts.length > 1 ? `0 0 0 ${DOT_RING}px ${SURFACE_CONTAINER_LOWEST}` : undefined,
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
            <Typography
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.04em',
                color: alpha(NAVY, 0.5),
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {moment(alert.occurredAt).format('DD/MM · HH:mm')}
            </Typography>
            {/* 0.35 on this surface is ~2.2:1, below AA even for large text; 0.7 clears it. */}
            <Typography sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.72rem', color: alpha(NAVY, 0.7) }}>
              {CATEGORY_LABELS[alert.category] ?? alert.category}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontSize: '0.72rem',
                fontWeight: 600,
                color: SEVERITY_COLORS[alert.severity],
              }}
            >
              {SEVERITY_LABELS[alert.severity]}
            </Typography>
          </Box>
          <Typography sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.95rem', color: NAVY, mt: 0.25 }}>
            {alert.title}
          </Typography>
          {/* Meta's raw restriction code. Unreadable on its own, which is why the title carries
              the plain-language version — but it is the identifier an appeal has to quote. */}
          {alert.violationType && (
            <Typography
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontSize: '0.72rem',
                color: alpha(NAVY, 0.7),
                mt: 0.25,
                wordBreak: 'break-word',
              }}
            >
              {alert.violationType}
            </Typography>
          )}
        </Box>
      </Box>
    ))}
  </Box>
);

/** Centred notice on a tonal surface — the same empty-state treatment the Leads dashboard uses. */
const Notice: React.FC<{ title: string; detail?: string }> = ({ title, detail }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      mt: 4,
      py: { xs: 8, md: 10 },
      px: 2,
      backgroundColor: SURFACE_CONTAINER_LOW,
      borderRadius: RADIUS_COIN,
      textAlign: 'center',
    }}
  >
    <Typography
      sx={{
        fontFamily: 'Jost, sans-serif',
        fontWeight: 700,
        fontSize: { xs: '1.1rem', md: '1.35rem' },
        color: NAVY,
        maxWidth: 520,
      }}
    >
      {title}
    </Typography>
    {detail && (
      <Typography
        sx={{ fontFamily: 'Jost, sans-serif', color: alpha(NAVY, 0.6), fontSize: '0.95rem', mt: 1, maxWidth: 520 }}
      >
        {detail}
      </Typography>
    )}
  </Box>
);

/**
 * Big Idea: whether this business unit's WhatsApp number is at risk of losing sending
 * capacity. The hero state is always live; the history honours the date range.
 */
const WhatsAppHealthContent: React.FC<{ businessUnitId: number }> = ({ businessUnitId }) => {
  const filterConfig = useMemo<FilterFieldConfig[]>(
    () => [
      {
        key: 'dateRange',
        kind: 'dateRange',
        label: 'Fecha',
        placement: 'bar',
        defaultValue: {
          start: moment().subtract(7, 'days').startOf('day'),
          end: moment().endOf('day'),
        } satisfies DateRangeValue,
      },
    ],
    []
  );

  // Namespaced so the range is deep-linkable and cannot collide with another surface using the
  // same `dateRange` key.
  const filter = useMasterFilter(filterConfig, businessUnitId, undefined, undefined, {
    urlNamespace: 'whatsAppHealth',
  });
  const dateRange = (filter.values.dateRange as DateRangeValue | undefined) ?? { start: null, end: null };
  const startDate = dateRange.start;
  const endDate = dateRange.end;

  const { data, isLoading, error } = useGetWhatsAppHealthQuery(
    {
      businessUnitId,
      startDate: startDate?.format(API_DATE_FORMAT) ?? '',
      endDate: endDate?.format(API_DATE_FORMAT) ?? '',
    },
    { skip: !startDate || !endDate }
  );
  const [refresh, { isLoading: isRefreshing, data: refreshResult, error: refreshError, reset: resetRefresh }] =
    useRefreshWhatsAppAlertsMutation();
  // The base query resolves with `{ error }` instead of throwing, so a failed refresh leaves
  // `refreshResult` undefined and would otherwise look exactly like a successful one. Both the
  // transport failure and the server's own "360 was unreachable" answer have to be surfaced.
  const syncFailed = refreshResult?.synced === false || !!refreshError;
  const [typeFilter, setTypeFilter] = useState<string>(ALL_TYPES);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { data: alertsPage, isFetching: isFetchingAlerts } = useGetWhatsAppAlertsQuery(
    {
      businessUnitId,
      startDate: startDate?.format(API_DATE_FORMAT) ?? '',
      endDate: endDate?.format(API_DATE_FORMAT) ?? '',
      category: typeFilter === ALL_TYPES ? undefined : typeFilter,
      page,
      pageSize,
    },
    { skip: !startDate || !endDate }
  );

  // Ask Meta for this unit's live number status when the page opens and whenever the selected
  // unit changes. Gated on `usesDialog360` so units without that channel never trigger the poll,
  // and keyed by unit so the tag invalidation that follows cannot re-arm the effect.
  const polledForRef = useRef<number | null>(null);
  useEffect(() => {
    if (!data?.usesDialog360 || polledForRef.current === businessUnitId) return;
    polledForRef.current = businessUnitId;
    refresh({ businessUnitId });
  }, [businessUnitId, data?.usesDialog360, refresh]);

  // Without this, a failed sync on one unit keeps its warning above the next unit's data — the
  // effect above early-returns for a unit with no 360 channel, so nothing else clears it.
  useEffect(() => {
    resetRefresh();
    setTypeFilter(ALL_TYPES);
  }, [businessUnitId, resetRefresh]);

  // Page 4 of a filter that now has one page would render empty with no way back. Anything that
  // changes the underlying set has to send the reader to the beginning of it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the range and filter are the trigger.
  useEffect(() => {
    setPage(0);
  }, [businessUnitId, typeFilter, startDate?.valueOf(), endDate?.valueOf(), pageSize]);

  const current = data?.current;

  const headline = useMemo(() => {
    if (!current) return 'Salud del número de WhatsApp';
    if (current.restricted) return 'La cuenta está restringida por Meta';
    if (current.quality === 'RED') return 'La calidad del número está en rojo';
    if (current.quality === 'YELLOW') return 'La calidad del número bajó a media';
    if (current.quality === 'GREEN') return 'El número está sano';
    return 'Todavía no hay señales de calidad para este número';
  }, [current]);

  const periodLabel =
    startDate && endDate ? `${startDate.format('DD/MM/YYYY')} – ${endDate.format('DD/MM/YYYY')}` : 'Período completo';

  const eventCounts = data?.eventCounts ?? { critical: 0, warning: 0, info: 0 };

  // One cell per day, coloured by that day's rating — the same read WhatsApp Manager gives for a
  // single number, without leaving the product.
  const history = useMemo(() => {
    if (!data) return null;
    return data.qualityHistory.some((point) => point.quality !== 'UNKNOWN') ? data.qualityHistory : null;
  }, [data]);

  // The filter bar and refresh control stay mounted across every state so they never disappear
  // from under the user mid-request.
  const controls = (
    <>
      <MasterFilter {...filter} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1.5 }}>
        <Tooltip title="Actualizar desde Meta" arrow>
          <span>
            <Box
              component="button"
              type="button"
              onClick={() => {
                refresh({ businessUnitId }).catch(() => {});
              }}
              // Only disabled once we know the unit has no 360 channel. Keying it off `!data`
              // would close the retry path in the error state, which is the one place the copy
              // below tells the user to press it.
              disabled={isRefreshing || (!!data && !data.usesDialog360)}
              aria-label="Actualizar desde Meta"
              sx={{
                display: 'grid',
                placeItems: 'center',
                width: 34,
                height: 34,
                border: 'none',
                borderRadius: '50%',
                cursor: 'pointer',
                backgroundColor: 'transparent',
                color: alpha(NAVY, 0.55),
                transition: 'all 0.15s ease-in-out',
                '&:hover:not(:disabled)': { backgroundColor: alpha(NAVY, 0.06), color: NAVY },
                '&:disabled': { cursor: 'default', color: alpha(NAVY, 0.25) },
              }}
            >
              {isRefreshing ? <CircularProgress size={16} sx={{ color: NAVY }} /> : <RefreshIcon fontSize="small" />}
            </Box>
          </span>
        </Tooltip>
      </Box>
      {syncFailed && (
        <Alert severity="warning" sx={{ borderRadius: RADIUS_CARD, mt: 1.5 }}>
          No se pudo obtener la información.
        </Alert>
      )}
    </>
  );

  if (isLoading) {
    return (
      <Box>
        {controls}
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: NAVY }} />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        {controls}
        <Notice title="No pudimos cargar la salud del número" detail="Volvé a intentarlo con el botón de actualizar." />
      </Box>
    );
  }

  // The picker commits `{ start, end: null }` after the first of its two clicks, which skips the
  // query. Returning null here would unmount the filter bar and the open calendar mid-selection,
  // and the partial range is persisted to the URL, so a reload would not recover either.
  if (!startDate || !endDate) {
    return (
      <Box>
        {controls}
        <Notice title="Elegí un rango de fechas para ver la salud del número" />
      </Box>
    );
  }

  if (!data || !current) {
    return (
      <Box>
        {controls}
        <Notice title="No hay información para este rango" />
      </Box>
    );
  }

  if (!data.usesDialog360) {
    return (
      <Box>
        {controls}
        <Notice title="Esta información no está disponible" />
      </Box>
    );
  }

  const limit = formatLimit(current.currentLimit);
  // Counts come from the server over the whole period, never from the page in hand: deriving
  // them from a truncated list would make the tabs contradict the totals on the cards above.
  const categoryCounts = alertsPage?.categoryCounts ?? [];
  const typeOptions: Array<PillTab<string>> = [
    { value: ALL_TYPES, label: 'Todas', count: categoryCounts.reduce((sum, entry) => sum + entry.count, 0) },
    ...categoryCounts.map((entry) => ({
      value: entry.category,
      label: CATEGORY_LABELS[entry.category] ?? entry.category,
      count: entry.count,
    })),
  ];
  const visibleAlerts = alertsPage?.alerts ?? [];
  const alertsTotal = alertsPage?.total ?? 0;

  return (
    <Box>
      {controls}

      <DashboardHero
        segment={data.businessUnitName}
        metricLabel="Calidad del número"
        headline={headline}
        value={QUALITY_LABELS[current.quality]}
        valueColor={QUALITY_HERO_COLORS[current.quality]}
        context={
          current.lastEventTitle ? (
            <>
              Último evento: <strong style={{ fontWeight: 600 }}>{current.lastEventTitle}</strong>
              {current.lastEventAt ? `, ${relativeTimeEs(current.lastEventAt)}.` : '.'}
            </>
          ) : (
            'Todavía no registramos eventos para este número.'
          )
        }
        supporting={
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gridAutoRows: '1fr',
              gap: 1.5,
            }}
          >
            <StatusCard label="Límite diario" value={limit.value} caption={limit.caption} />
            <StatusCard
              label="Restricciones"
              value={current.restricted ? 'Restringida' : 'Ninguna'}
              caption={current.restricted ? 'requiere acción' : 'envío habilitado'}
              accent={current.restricted ? STATUS_ERROR : undefined}
            />
            <StatusCard
              label="Alertas críticas"
              value={eventCounts.critical.toLocaleString('es-CL')}
              caption="en el período"
            />
            <StatusCard
              label="Advertencias"
              value={eventCounts.warning.toLocaleString('es-CL')}
              caption="en el período"
            />
          </Box>
        }
      />

      <ChartCard
        eyebrow="Tendencia"
        title="Evolución de la calidad"
        description="Cada celda es un día. Los días sin eventos mantienen la última calidad conocida."
        actions={<QualityLegend />}
        footer={`${periodLabel} · ${data.businessUnitName}${data.phoneNumber ? ` · ${data.phoneNumber}` : ''}`}
      >
        {history ? (
          <>
            {/* Height is a second, redundant encoding of the same rating. Hue alone would be
                unreadable for a deuteranope, since good and bad sit at the two ends of a
                red/green ramp — with height, worse ratings read as shorter bars regardless.
                The gap also has to shrink as the window widens: at 90 days on a narrow phone a
                fixed 3px gap consumes the entire width and leaves sub-pixel slivers. */}
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: STRIP_GAP(history.length), height: 56 }}>
              {history.map((point) => (
                <Box
                  key={point.day}
                  title={`${moment(point.day).format('DD/MM/YYYY')}: ${
                    point.quality === 'UNKNOWN' ? 'Sin datos' : QUALITY_LABELS[point.quality]
                  }`}
                  sx={{
                    flex: 1,
                    minWidth: 2,
                    height: QUALITY_HEIGHTS[point.quality],
                    borderRadius: RADIUS_ITEM,
                    // Meta's own green/yellow/red is the data here, but softened so a month of
                    // cells sits inside the navy palette instead of reading as a traffic light.
                    backgroundColor:
                      point.quality === 'UNKNOWN' ? QUALITY_COLORS.UNKNOWN : alpha(QUALITY_COLORS[point.quality], 0.8),
                  }}
                />
              ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', color: alpha(NAVY, 0.5) }}>
                {moment(history[0].day).format('DD/MM')}
              </Typography>
              <Typography sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', color: alpha(NAVY, 0.5) }}>
                {moment(history[history.length - 1].day).format('DD/MM')}
              </Typography>
            </Box>
          </>
        ) : (
          <Notice title="Todavía no registramos cambios de calidad para este número" />
        )}
      </ChartCard>

      <ChartCard
        eyebrow="Ocurrencias"
        title="Línea de tiempo de alertas"
        description="Lo más reciente que Meta reportó sobre este número, de lo nuevo a lo viejo."
        actions={
          typeOptions.length > 1 ? (
            <PillTabs
              tabs={typeOptions}
              value={typeFilter}
              onChange={setTypeFilter}
              ariaLabel="Filtrar alertas por tipo"
              wrap
            />
          ) : undefined
        }
        footer={`${periodLabel} · solo advertencias y alertas críticas`}
      >
        {visibleAlerts.length > 0 ? (
          <>
            <Box sx={{ opacity: isFetchingAlerts ? 0.5 : 1, transition: 'opacity 0.15s ease-in-out' }}>
              <AlertTimeline alerts={visibleAlerts} />
            </Box>
            {alertsTotal > pageSize && (
              <PaginationFooter
                page={page}
                pageSize={pageSize}
                rowCount={alertsTotal}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                // The shared footer pins itself to the viewport on mobile for full-page tables;
                // inside a card it has to stay in flow.
                sx={{ position: 'static', px: 0, justifyContent: 'flex-end' }}
              />
            )}
          </>
        ) : (
          <Notice
            title={typeFilter === ALL_TYPES ? 'Sin alertas en este período' : 'Sin alertas de este tipo en el período'}
            detail={
              typeFilter === ALL_TYPES
                ? 'Ninguna advertencia ni alerta crítica para este número.'
                : 'Probá con otro tipo o ampliá el rango de fechas.'
            }
          />
        )}
      </ChartCard>
    </Box>
  );
};

/**
 * Report on the health of a business unit's WhatsApp number. The unit comes from the sidebar
 * selector, mirroring how the Dashboard scopes itself.
 */
const WhatsAppHealth: React.FC = () => {
  const { selectedBusinessUnitId: businessUnitId, setSelectedBusinessUnitId } = useSelectedBusinessUnit();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const paramId = searchParams.get('businessUnitId');
    if (paramId) {
      const parsed = Number.parseInt(paramId, 10);
      if (!Number.isNaN(parsed)) setSelectedBusinessUnitId(parsed);
    }
    // Only run on mount — intentionally omitting deps
  }, []);

  const hasBusinessUnit = businessUnitId !== null && businessUnitId !== -1;

  return (
    <Box sx={{ px: { xs: 2, lg: 4 }, pt: { xs: `${MOBILE_PAGE_PT}px`, md: 3 }, pb: 6 }}>
      {hasBusinessUnit ? (
        <WhatsAppHealthContent businessUnitId={businessUnitId} />
      ) : (
        <Notice
          title="Selecciona una unidad de negocio para ver la salud de su número"
          detail="Calidad del número, límite de mensajería y restricciones reportadas por Meta."
        />
      )}
    </Box>
  );
};

export default WhatsAppHealth;
